import { useState, useRef } from 'react';
import { useForm, useStore } from '@tanstack/react-form';
import { templateHooks } from '../network/endpoints';
import { TemplateCreateSchema, type TemplateDetail, type Channel, type Locale } from '../network/types';
import { FormField, Input } from '../../../components/ui/FormField';
import { Button } from '../../../components/ui/Button';
import { cn } from '@repo/utils';
import { useToast } from '../../../components/ui/Toast';
import { fieldError } from '@repo/web-form';
import { z } from 'zod';

const validate = (schema: z.ZodType<any>, value: any) => {
  const res = schema.safeParse(value);
  return res.success ? undefined : res.error.issues[0].message;
};

interface TemplateEditorProps {
  initialData?: TemplateDetail;
  onSave?: () => void;
  onCancel?: () => void;
}

export function TemplateEditor({ initialData, onSave, onCancel }: TemplateEditorProps) {
  const [activeLocale, setActiveLocale] = useState<Locale>(initialData?.locale || 'en');
  // Local state to keep track of both locale bodies before final submission
  const [localeBodies, setLocaleBodies] = useState<Record<Locale, string>>({
    en: initialData?.locale === 'en' ? initialData.bodyTemplate : '',
    ta: initialData?.locale === 'ta' ? initialData.bodyTemplate : '',
  });

  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);

  const createMutation = templateHooks.useCreate();
  const updateMutation = templateHooks.useUpdate();
  const { toast } = useToast();

  const form = useForm({
    defaultValues: {
      templateKey: initialData?.templateKey || '',
      channel: (initialData?.channel || 'SMS') as Channel,
      locale: activeLocale,
      subject: initialData?.subject || '',
      bodyTemplate: localeBodies[activeLocale],
      dltTemplateId: initialData?.dltTemplateId || '',
      isActive: initialData?.isActive ?? true,
    },
    onSubmit: async ({ value }) => {
      // Manual validation as fallback
      const result = TemplateCreateSchema.safeParse({
        ...value,
        bodyTemplate: localeBodies[activeLocale],
      });
      
      if (!result.success) {
        toast({ type: 'error', title: 'Validation Error', description: result.error.issues[0]?.message || 'Validation failed' });
        return;
      }

      const payload = {
        ...value,
        bodyTemplate: localeBodies[activeLocale],
        subject: value.subject || null,
        dltTemplateId: value.dltTemplateId || null,
      };

      try {
        if (initialData) {
          await updateMutation.mutateAsync({
            id: initialData.id,
            body: payload as any,
          });
        } else {
          await createMutation.mutateAsync(payload as any);
        }
        toast({ type: 'success', title: 'Success', description: 'Template saved successfully.' });
        onSave?.();
      } catch (err: any) {
        console.error('Failed to save template', err);
        toast({ type: 'error', title: 'Error', description: err.message || 'Failed to save template.' });
      }
    },
  });

  const isSubmitting = useStore(form.store, (s) => s.isSubmitting);
  const submissionAttempts = useStore(form.store, (s) => s.submissionAttempts);

  const currentBody = localeBodies[activeLocale];

  const insertVariable = (v: string) => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = currentBody;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newBody = `${before}{{${v}}}${after}`;

    setLocaleBodies((prev) => ({ ...prev, [activeLocale]: newBody }));
    form.setFieldValue('bodyTemplate', newBody);

    // Focus back and set cursor
    setTimeout(() => {
      textarea.focus();
      const newPos = start + v.length + 4;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleLocaleChange = (newLocale: Locale) => {
    // Sync current body to state before switching
    setLocaleBodies((prev) => ({ ...prev, [activeLocale]: form.getFieldValue('bodyTemplate') }));
    setActiveLocale(newLocale);
    // Locale is part of the form, but we keep bodies separate for draft preservation
    form.setFieldValue('locale', newLocale);
    form.setFieldValue('bodyTemplate', localeBodies[newLocale]);
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-6"
    >
      <div className="grid grid-cols-2 gap-4">
        <form.Field
          name="templateKey"
          validators={{ onChange: ({ value }) => validate(TemplateCreateSchema.shape.templateKey, value) }}
          children={(field) => (
            <FormField
              label="Template Key"
              required
              error={fieldError(field.state.meta.errors, submissionAttempts)}
            >
              <Input
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="e.g. auth.otp.login"
                disabled={!!initialData || isSubmitting}
              />
            </FormField>
          )}
        />

        <form.Field
          name="channel"
          validators={{ onChange: ({ value }) => validate(TemplateCreateSchema.shape.channel, value) }}
          children={(field) => (
            <FormField
              label="Channel"
              required
              error={fieldError(field.state.meta.errors, submissionAttempts)}
            >
              <select
                className="w-full h-9 px-3 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value as Channel)}
                disabled={isSubmitting}
              >
                <option value="SMS">SMS</option>
                <option value="PUSH">Push</option>
                <option value="EMAIL">Email</option>
              </select>
            </FormField>
          )}
        />
      </div>

      <div className="flex items-center gap-2 border-b border-border pb-2">
        <Button
          type="button"
          variant={activeLocale === 'en' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => handleLocaleChange('en')}
        >
          English
        </Button>
        <Button
          type="button"
          variant={activeLocale === 'ta' ? 'secondary' : 'ghost'}
          size="sm"
          onClick={() => handleLocaleChange('ta')}
        >
          Tamil
        </Button>
      </div>

      <form.Subscribe selector={(s) => s.values.channel}>
        {(channelValue) => (
          <>
            {channelValue === 'EMAIL' && (
              <form.Field
                name="subject"
                validators={{ onChange: ({ value }) => validate(TemplateCreateSchema.shape.subject, value) }}
                children={(field) => (
                  <FormField
                    label="Email Subject"
                    required
                    error={fieldError(field.state.meta.errors, submissionAttempts)}
                  >
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Subject line"
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            )}

            {channelValue === 'SMS' && (
              <form.Field
                name="dltTemplateId"
                validators={{ onChange: ({ value }) => validate(TemplateCreateSchema.shape.dltTemplateId, value) }}
                children={(field) => (
                  <FormField
                    label="DLT Template ID"
                    required
                    error={fieldError(field.state.meta.errors, submissionAttempts)}
                    hint="Required for SMS channel"
                  >
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="DLT ID from portal"
                      disabled={isSubmitting}
                    />
                  </FormField>
                )}
              />
            )}
          </>
        )}
      </form.Subscribe>

      <form.Field
        name="bodyTemplate"
        validators={{ onChange: ({ value }) => validate(TemplateCreateSchema.shape.bodyTemplate, value) }}
        children={(field) => {
          const body = field.state.value;
          const length = body.length;
          const segments = length <= 160 ? 1 : Math.ceil(length / 153);
          
          // Variables extraction
          const matches = body.matchAll(/{{(.*?)}}/g);
          const variables = Array.from(new Set(Array.from(matches).map((m) => m[1].trim())));

          return (
            <FormField
              label="Body Template"
              required
              error={fieldError(field.state.meta.errors, submissionAttempts)}
              hint={
                form.state.values.channel === 'SMS'
                  ? `${length} characters, will send as ${segments} concatenated SMS`
                  : undefined
              }
            >
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2 mb-2">
                  {variables.map((v) => (
                    <Button
                      key={v}
                      type="button"
                      variant="secondary"
                      onClick={() => insertVariable(v)}
                      className="px-2 py-1 bg-surface-2 hover:bg-surface-1 border border-border rounded text-xs font-mono text-text-secondary"
                    >
                      {`{{${v}}}`}
                    </Button>
                  ))}
                </div>
                <textarea
                  ref={bodyTextareaRef}
                  className={cn(
                    'w-full min-h-[120px] px-3 py-2 bg-surface-3 border border-border rounded-[6px] text-sm text-text-primary placeholder:text-text-muted',
                    'focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500',
                    form.state.values.channel === 'SMS' && length > 160 && 'border-warning-500 focus:border-warning-500 focus:ring-warning-500',
                    form.state.values.channel === 'SMS' && length > 1600 && 'border-danger-500 focus:border-danger-500 focus:ring-danger-500'
                  )}
                  value={body}
                  onChange={(e) => {
                    const val = e.target.value;
                    setLocaleBodies((prev) => ({ ...prev, [activeLocale]: val }));
                    field.handleChange(val);
                  }}
                  disabled={isSubmitting}
                />
              </div>
            </FormField>
          );
        }}
      />

      <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
          Save Template
        </Button>
      </div>
    </form>
  );
}
